import type { GetSelectedComponent } from "./gridUtils.type";
import type { Int } from "@/types";

import { t } from "i18next";

import { parseMultiLevelsValue } from "@/components/Elements/HierarchicalComponents/hierarchicalComponentsUtils";
import {
  DEFAULT_NUMBER_OF_LEVELS,
  SeverityFieldNames,
} from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/constants";
import { getLevelDisplayLabel } from "@/components/FormEngine/Editor/DragAndDropTool/Field/Severity/mappers";

import { buildSlugNameItems } from "./gridUtils.helper";

export type GridSampleItem = { name: string; value: number };

/**
 * Generate drop down list string values of numeric sequence
 *
 * @param {number} [length=0] - The size of the list
 *
 * @return {string[]} - The list values (Example: ["1", "2", ...])
 */
export const generateDropDownNumericValues = (length: Int = 0) =>
  !length || isNaN(length)
    ? []
    : Array.from({ length }, (_, index) => `${index + 1}`);

export const visibleWhenFieldPopulated = (model = "") => ({
  model,
  value: "",
  condition: "!=",
});

const buildSampleCriteria = () => {
  const choices = [];

  for (let level = 1; level <= DEFAULT_NUMBER_OF_LEVELS; level++) {
    const value = `${level}`;
    const name = getLevelDisplayLabel(level);
    const description = t(
      "form:staticField.severity.formEditorSample.description",
      { level }
    );
    const disabled = false;
    choices.push({ value, name, description, disabled });
  }

  // demo of a disabled option
  if (DEFAULT_NUMBER_OF_LEVELS > 3) {
    choices[3].disabled = true;
    choices[3].description = t(
      "form:staticField.severity.formEditorSample.descriptionForDisabled",
      { level: 4 }
    );
  }

  return choices;
};

export const getSelectedComponent: GetSelectedComponent = ({
  selection = "",
  severityGrid = [],
}) => {
  if (!selection || !severityGrid?.length) {
    return undefined;
  }

  const selectedSlugs = parseMultiLevelsValue(selection);
  const [firstSlug] = selectedSlugs;
  let component = severityGrid.find(({ slug }) => slug === firstSlug);

  for (let i = 1; i < selectedSlugs.length; i++) {
    const slugItem = selectedSlugs[i];
    component = component?.components?.find(({ slug }) => slug === slugItem);
  }

  return component;
};

const buildSampleMaterials = (length = DEFAULT_NUMBER_OF_LEVELS) =>
  buildSlugNameItems(length, t("form:staticField.material.label"));

const buildSampleDefects = (length = DEFAULT_NUMBER_OF_LEVELS) =>
  buildSlugNameItems(length, t("form:staticField.defect.label"));

const buildSampleDefectsSuggestions = () => {
  const sample_defects = buildSampleDefects(DEFAULT_NUMBER_OF_LEVELS);
  const defects = [];
  defects.push({
    defect: sample_defects[0].slug,
    name: sample_defects[0].name,
    confidence: 0.9,
    suggested: true,
  });
  defects.push({
    defect: sample_defects[1].slug,
    name: sample_defects[1].name,
    confidence: 0.7,
    suggested: true,
  });
  defects.push({
    defect: sample_defects[2].slug,
    name: sample_defects[2].name,
    confidence: 0.1,
    suggested: false,
  });
  defects.push({
    defect: sample_defects[3].slug,
    name: sample_defects[3].name,
    confidence: 0.05,
    suggested: false,
  });
  return {
    defects,
  };
};

const buildSampleNameValues = (field: string) =>
  Array.from(
    { length: 5 },
    (_, i): GridSampleItem => ({
      value: i + 1,
      name: t(`form:staticField.${field}.levels.${i + 1}`),
    })
  );

export const SeverityGridPreview = {
  buildSampleMaterials,
  buildSampleDefects,
  buildSampleDefectsSuggestions,
  buildSampleCriteria,
  buildSampleProbabilities: () => buildSampleNameValues("probability"),
  buildSampleConsequences: () => buildSampleNameValues("consequence"),
  buildSampleRisks: () => buildSampleNameValues("risk"),
};

const visibleWhenDefectIsPopulated = () => [
  visibleWhenFieldPopulated(SeverityFieldNames.defect),
];

const buildComponent = () => ({
  type: "inspectedComponent",
  model: SeverityFieldNames.component,
  isFrozen: true,
  values: [
    t("form:staticField.component.mainLevelSample"),
    t("form:staticField.component.subLevelSample"),
    "…",
  ],
  label: t("form:staticField.component.label"),
  placeholder: t("form:staticField.component.placeholder"),
});

const buildMaterial = (
  materialValues: {
    id: string;
    name: string;
  }[] = []
) => ({
  type: "materials",
  model: SeverityFieldNames.material,
  isFrozen: true,
  values: materialValues,
  label: t("form:staticField.material.label"),
  placeholder: t("form:staticField.material.placeholder"),
});
const buildDefect = () => ({
  type: "defects",
  model: SeverityFieldNames.defect,
  isFrozen: true,
  values: [t("form:staticField.defect.dynamicValues")],
  label: t("form:staticField.defect.label"),
  placeholder: t("form:staticField.defect.placeholder"),
  visible: [visibleWhenFieldPopulated(SeverityFieldNames.material)],
});

const buildSeverityCriteria = () => ({
  type: "severities",
  model: SeverityFieldNames.severity,
  isFrozen: true,
  values: [t("form:staticField.severity.dynamicValues")],
  label: t("form:staticField.severity.label"),
  placeholder: t("form:staticField.severity.placeholder"),
  visible: visibleWhenDefectIsPopulated(),
});

const buildProbability = (dropDownValues: string[] = []) => ({
  type: "select",
  model: SeverityFieldNames.probability,
  isFrozen: true,
  values: dropDownValues,
  label: t("form:staticField.probability.label"),
  placeholder: t("form:staticField.probability.placeholder"),
  visible: visibleWhenDefectIsPopulated(),
});

const buildConsequence = (dropDownValues: string[] = []) => ({
  type: "select",
  model: SeverityFieldNames.consequence,
  isFrozen: true,
  values: dropDownValues,
  label: t("form:staticField.consequence.label"),
  placeholder: t("form:staticField.consequence.placeholder"),
  visible: visibleWhenDefectIsPopulated(),
});

const buildRisk = (dropDownValues: string[] = []) => ({
  type: "select",
  model: SeverityFieldNames.risk,
  isFrozen: true,
  values: dropDownValues,
  label: t("form:staticField.risk.label"),
  placeholder: t("form:staticField.risk.placeholder"),
  visible: visibleWhenDefectIsPopulated(),
});

export const buildSeverityGroup = (
  materialValues: { id: string; name: string }[] = []
) => {
  const dropDownValues = buildDefaultLevels();

  const component = buildComponent();
  const material = buildMaterial(materialValues);
  const defect = buildDefect();
  const severity = buildSeverityCriteria();
  const probability = buildProbability(dropDownValues);
  const consequence = buildConsequence(dropDownValues);
  const risk = buildRisk(dropDownValues);

  const group = {
    fields: [
      component,
      material,
      defect,
      severity,
      probability,
      consequence,
      risk,
    ],
    legend: t("form:severityGrid.groupName"),
    readonly: true,
    unclonable: true,
    droppable: true,
    draggable: true,
  };

  return group;
};

export const buildDefaultLevels = () =>
  generateDropDownNumericValues(DEFAULT_NUMBER_OF_LEVELS);

const buildDisabledCriterion = (level: number) => ({
  level,
  description: t("form:staticField.severity.disabledLevel"),
  disabled: true,
});

export const applySeverityGridRules = (
  criteria: {
    level: number;
    description?: string;
    disabled?: boolean;
  }[] = []
) => {
  const severityCriteria = [];

  for (let level = 1; level <= DEFAULT_NUMBER_OF_LEVELS; level++) {
    const criterion = criteria.find((criterion) => criterion.level === level);
    if (criterion?.description) {
      severityCriteria.push(criterion);
    } else {
      severityCriteria.push(buildDisabledCriterion(level));
    }
  }

  return severityCriteria;
};
